<template>
	<v-menu
		v-model="isOpened"
		:disabled="!shouldShowProjectActionFAB || disabled"
		content-class="fab-action-menu"
		:close-on-content-click="true"
		:persistent="!closeOnClick"
		:nudge-bottom="64"
		:min-width="350"
		tile
		bottom
		:z-index="2000"
		@input="handleInputChange">
		<template #activator="{ props }">
			<v-btn
				v-test="'project-add-content-button'"
				v-track="'project-add-content-button'"
				v-bind="props"
				aria-label="Add Content"
				icon
				elevation="0"
				:disabled="disabled"
				@click="fabToggle">
				<v-icon>icon-plus</v-icon>
			</v-btn>
		</template>

		<v-list v-if="isOpened" rounded="lg">
			<v-list-item class="fab-action">
				<template v-if="isMobile">
					<div class="d-flex align-center justify-start w-100 cursor-pointer" @click="navigateToAction('upload-photo')">
						<v-btn :class="'action-fab-upload-photo'" icon>
							<v-icon class="action-button-icon">icon-cloud-stroke-upload</v-icon>
						</v-btn>
						<span class="fab-action-header flex">Add Photos, Documents, or Videos</span>
					</div>
				</template>
				<ActionButton
					v-else
					:button-id="'fab-action-sync'"
					:project-id="projectId"
					:project="project"
					:button-text="'Add Photos, Documents, or Videos'"
					:button-icon="'icon-cloud-stroke-upload'"
					:button-type="'textFab'"
					:action="'upload-photo'"
					@close-parent-menu="closeMenu"
					@uploadCounts="(counts) => $emit('uploadCounts', counts)">
				</ActionButton>
			</v-list-item>
			<v-list-item class="fab-action">
				<template v-if="isMobile">
					<div class="d-flex align-center justify-start w-100 cursor-pointer" @click="navigateToAction('photo')">
						<v-btn :class="'action-fab-photo'" icon>
							<v-icon class="action-button-icon">icon-camera-solid</v-icon>
						</v-btn>
						<span class="fab-action-header flex">Take Photos</span>
					</div>
				</template>
				<ActionButton
					v-else
					:button-id="'fab-action-sync'"
					:project-id="projectId"
					:project="project"
					:button-text="'Take Photos'"
					:button-icon="'icon-camera-solid'"
					:button-type="'textFab'"
					:action="'photo'"
					@close-parent-menu="closeMenu"></ActionButton>
			</v-list-item>
			<v-list-item class="fab-action">
				<template v-if="isMobile">
					<div class="d-flex align-center justify-start w-100 cursor-pointer" @click="navigateToAction('request-recording')">
						<v-btn :class="'action-fab-request-recording'" icon>
							<v-icon class="action-button-icon">icon-paper-plane-stroke</v-icon>
						</v-btn>
						<span class="fab-action-header flex">Request Stuff/Content</span>
					</div>
				</template>
				<ActionButton
					v-else
					:button-id="'fab-action-sync'"
					:project-id="projectId"
					:project="project"
					:button-text="'Request Stuff/Content'"
					:button-icon="'icon-paper-plane-stroke'"
					:button-type="'textFab'"
					:action="'request-recording'"
					@close-parent-menu="closeMenu"></ActionButton>
			</v-list-item>
			<v-list-item class="fab-action">
				<template v-if="isMobile">
					<div class="d-flex align-center justify-start w-100 cursor-pointer" @click="navigateToAction('async-recording')">
						<v-btn :class="'action-fab-async-recording'" icon>
							<v-icon class="action-button-icon">icon-recording-circle</v-icon>
						</v-btn>
						<span class="fab-action-header flex">Start a Recording</span>
					</div>
				</template>
				<ActionButton
					v-else
					:button-id="'fab-action-sync'"
					:project-id="projectId"
					:project="project"
					:button-text="'Start a Recording'"
					:button-icon="'icon-recording-circle'"
					:button-type="'textFab'"
					:action="'async-recording'"
					@close-parent-menu="closeMenu">
				</ActionButton>
			</v-list-item>
			<v-list-item class="fab-action">
				<template v-if="isMobile">
					<div class="d-flex align-center justify-start w-100 cursor-pointer" @click="navigateToAction('schedule')">
						<v-btn :class="'action-fab-schedule'" icon>
							<v-icon class="action-button-icon">icon-calendar-stroke</v-icon>
						</v-btn>
						<span class="fab-action-header flex">Schedule a Live Video Call</span>
					</div>
				</template>
				<ActionButton
					v-else
					:button-id="'fab-action-sync'"
					:project-id="projectId"
					:project="project"
					:button-text="'Schedule a Live Video Call'"
					:button-icon="'icon-calendar-stroke'"
					:button-type="'textFab'"
					:action="'schedule'"
					@close-parent-menu="closeMenu"></ActionButton>
			</v-list-item>
			<v-list-item class="fab-action">
				<template v-if="isMobile">
					<div class="d-flex align-center justify-start w-100 cursor-pointer" @click="navigateToAction('live-call')">
						<v-btn :class="'action-fab-live-call'" icon>
							<v-icon class="action-button-icon">icon-conversation-stroke</v-icon>
						</v-btn>
						<span class="fab-action-header flex">Start a Live Video Call Now</span>
					</div>
				</template>
				<ActionButton
					v-else
					:button-id="'fab-action-sync'"
					:project-id="projectId"
					:project="project"
					:button-text="'Start a Live Video Call Now'"
					:button-icon="'icon-conversation-stroke'"
					:button-type="'textFab'"
					:action="'live-call'"
					@close-parent-menu="closeMenu">
				</ActionButton>
			</v-list-item>
		</v-list>
	</v-menu>
</template>

<script>
	import ActionButton from "./ActionButton.vue";
	import { blurActiveElement } from "@/helpers/utilities";
	import { EventBus } from "../helpers/eventBus";
	import api from "../api";
	import DeviceUtility from "@/helpers/device";
	import { useDisplay } from "vuetify";

	export default {
		name: "ProjectFAB",
		components: {
			ActionButton
		},
		props: {
			projectId: {
				type: String,
				required: true
			},
			project: {
				type: Object,
				required: false
			},
			shouldShowProjectActionFAB: Boolean,
			disabled: Boolean
		},
		emits: ["conversation-created", "change", "uploadCounts", "fab-opened"],
		setup() {
			const display = useDisplay();
			return { display };
		},
		data() {
			return {
				isOpened: false,
				fabTab: 0,
				screenWidth: window.innerWidth,
				screenHeight: window.innerHeight,
				closeOnClick: true,
				me: null,
				_onDisableCloseOnClick: null,
				_onEnableCloseOnClick: null
			};
		},

		computed: {
			whiteBg() {
				return this.display.smAndDown.value ? "" : "white-bg";
			},
			isMobile() {
				return DeviceUtility.isMobile;
			}
		},
		async mounted() {
			this._onDisableCloseOnClick = () => {
				this.closeOnClick = false;
			};
			this._onEnableCloseOnClick = () => {
				this.closeOnClick = true;
			};
			EventBus.$on("DisableCloseOnClick", this._onDisableCloseOnClick);
			EventBus.$on("EnableCloseOnClick", this._onEnableCloseOnClick);
			try {
				this.me = await api.getMe();
			} catch (error) {
				console.error("ProjectActionFAB: failed to load user data", error);
			}
		},
		beforeUnmount() {
			EventBus.$off("DisableCloseOnClick", this._onDisableCloseOnClick);
			EventBus.$off("EnableCloseOnClick", this._onEnableCloseOnClick);
		},
		methods: {
			open() {
				this.isOpened = true;
			},
			close() {
				this.isOpened = false;
			},
			closeMenu(conversationId) {
				this.isOpened = false;
				console.log("ProjectActionFAB close " + conversationId);
				if (conversationId && typeof conversationId === "string") {
					this.$emit("conversation-created", conversationId);
				}
			},
			navigateToAction(action) {
				// LSP-15388 break-it round 1: this is the MOBILE entry point into the standalone
				// /upload (and /photo) pages, and the projectId it seeds is the only thing that can
				// bring the user back to the project afterwards. Routing with a blank/non-string id
				// produces a page that can never return anywhere, so refuse and say why instead.
				const projectId = typeof this.projectId === "string" ? this.projectId.trim() : "";
				if (!projectId) {
					console.warn(`ProjectActionFAB.navigateToAction: no usable projectId, refusing to open "${action}"`, {
						tags: { method: "navigateToAction", file: "ProjectActionFAB" },
						extra: { action, rawProjectId: this.projectId }
					});
					return;
				}
				// Closed only once the action is actually going somewhere. break-it round 3: closing it
				// first meant a refused action looked identical to a successful one — the menu vanished
				// and nothing happened.
				this.isOpened = false;
				// break-it round 2: a push can reject (a guard aborting) and can also throw synchronously
				// (missing router / unknown route name). Neither may escape a click handler — the FAB
				// menu has already closed, so an exception here leaves the user staring at the project
				// page with no idea why nothing opened.
				const warnNavigationFailed = (error) => {
					console.warn(`ProjectActionFAB.navigateToAction: navigation to "${action}" failed`, {
						tags: { method: "navigateToAction", file: "ProjectActionFAB" },
						extra: { action, projectId, error }
					});
				};
				try {
					const navigation = Promise.resolve(this.$router.push({ name: action, query: { projectId, from: "project" } }));
					// Both handlers are attached DIRECTLY to the push result, never to a derived promise:
					// vue-router does not reject an aborted, cancelled or duplicated navigation, it RESOLVES
					// with a NavigationFailure (an Error subclass), so a .catch alone never saw the most
					// likely real failure. Chaining .then().catch() instead would leave the ORIGINAL
					// rejection unconsumed. The .then rejection handler is a no-op because .catch already
					// reported it. break-it round 4
					navigation.catch(warnNavigationFailed);
					navigation.then(
						(outcome) => {
							if (outcome instanceof Error) {
								warnNavigationFailed(outcome);
							}
						},
						() => undefined
					);
				} catch (error) {
					warnNavigationFailed(error);
				}
			},
			fabToggle() {
				this.$emit("fab-opened");

				if (!this.shouldShowProjectActionFAB) {
					if (!this.me) {
						console.warn("ProjectActionFAB: fabToggle called before user data loaded, cannot open free-trial dialog");
						return;
					}
					this.$root.$freeTrialEndedDialog.open(this.me.organization);
				}
				blurActiveElement();
			},
			handleInputChange() {
				this.close();
				this.$emit("change", this.isOpened);
			}
		}
	};
</script>
<style>
	/* These styles can't be in scoped as they use variables from the root page */
	.action-fab-live-call {
		background-color: var(--live-call-fab) !important;
	}

	.action-fab-live-call i {
		color: var(--live-call-fab-icon) !important;
	}

	.action-fab-photo {
		background-color: var(--photo-fab) !important;
	}

	.action-fab-photo i {
		color: var(--photo-fab-icon) !important;
	}

	.action-fab-upload-photo {
		background-color: var(--upload-photo-fab) !important;
	}

	.action-fab-upload-photo i {
		color: var(--upload-photo-fab-icon) !important;
	}

	.action-fab-schedule {
		background-color: var(--schedule-fab) !important;
	}

	.action-fab-schedule i {
		color: var(--schedule-fab-icon) !important;
	}

	.action-fab-async-recording {
		background-color: var(--async-recording-fab) !important;
	}

	.action-fab-async-recording i {
		color: var(--async-recording-fab-icon) !important;
	}

	.action-fab-request-recording {
		background-color: var(--request-recording-fab) !important;
	}

	.action-fab-request-recording i {
		color: var(--request-recording-fab-icon) !important;
	}
</style>

<style scoped lang="scss">
	.fab-action :deep(.v-list-item__content) {
		width: 100%;
	}

	.menu .v-btn {
		height: 44px;
		width: 44px;
		border: 2px solid var(--primary-project-icon);
	}

	.menu .v-btn .v-icon {
		font-size: 1.75rem;
		color: var(--primary-project-icon);
	}

	.menu > .menu-action {
		width: 82px;
		align-items: center;
		padding: 8px;
	}

	.menu > .menu-action div:nth-child(2),
	.menu > .menu-action .text {
		margin-top: 8px;
		width: 40px;
		font-size: 0.625rem;
		font-weight: 500;
		text-align: center;
	}

	.fab-action {
		margin-top: 12px;
		display: flex;
		justify-content: flex-start;
	}

	.fab-action:first-child {
		margin-top: 0;
	}

	.fab-action:hover {
		background-color: #eff3fd;
	}

	.fab-action-header {
		text-align: left;
		font-weight: 700;
		margin-left: 16px;
		flex: 1;
	}

	#fab-button i {
		font-size: 32px;
	}

	#fab-button::v-deep > span {
		/* margin-left: -10px; */
	}

	.fab-menu {
		min-height: 100vh;
	}

	.fabTabCard {
		min-width: 368px;
	}
	.fab-menu::v-deep .v-tabs:not(.v-tabs--vertical) .v-tab {
		white-space: nowrap;
		font-size: 12px;
	}

	.fab-menu::v-deep .v-card {
		box-shadow: none;
	}

	.fab-menu::v-deep .contact-name.reset {
		padding-top: 0px;
	}

	@media (min-width: 320px) and #{map.get($display-breakpoints, 'sm-and-down')} and (orientation: landscape) {
		.fab-window {
			overflow: scroll;
		}
	}

	/* @media (min-width: 960px) {
		.v-application .v-menu__content.fab-action-menu {
			border-radius: 16px !important;
			left: 62vw !important;
		}
		#fab-button {
			bottom: 12px;
		}
	} */

	@media (max-width: 414px) {
		.fab-menu::v-deep .v-tabs:not(.v-tabs--vertical) .v-tab {
			font-size: 11px;
		}
	}

	@media (max-width: 375px) {
		.fab-menu::v-deep .v-tabs:not(.v-tabs--vertical) .v-tab {
			font-size: 10px;
		}
	}
</style>
<style lang="scss">
	/* Phone landscape: viewport height ≤ 500px (phones in landscape) but not desktop monitors.
	   Constrains the FAB action list so it doesn't overflow the short viewport.
	   80px accounts for the app bar height in landscape so the menu doesn't extend under it. */
	@media (orientation: landscape) and (max-height: 500px) {
		.fab-action-menu {
			max-height: calc(100vh - 80px) !important;
			overflow-y: auto !important;
		}
	}
</style>
